import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
    CheckCircle2, ShieldCheck, Lock, Loader2, XCircle,
    Mail as MailIcon, Building2, User as UserIcon,
} from "lucide-react";

/**
 * InviteAccept — public page a team member lands on after clicking the
 * invite link in their email. Preview → set password → account created →
 * redirect to login.
 *
 * URL: /invite/:token
 */
export default function InviteAccept() {
    const { token } = useParams();
    const nav = useNavigate();
    const [invite, setInvite] = useState(null);
    const [err, setErr] = useState(null);
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        api.get(`/users/invite/${token}`)
            .then((r) => setInvite(r.data))
            .catch((e) => setErr(formatApiError(e) || "Invite link is not valid."));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
        if (pw !== pw2)   { toast.error("Passwords don't match."); return; }
        setBusy(true);
        try {
            await api.post(`/users/invite/${token}/accept`, { password: pw });
            setDone(true);
            toast.success("Account created — you can sign in now.");
            setTimeout(() => nav("/login", { replace: true }), 1500);
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally { setBusy(false); }
    };

    if (err) {
        return (
            <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6" data-testid="invite-error">
                <div className="max-w-md w-full bg-white text-stone-900 rounded-2xl shadow-2xl p-8 text-center">
                    <XCircle size={28} className="mx-auto text-rose-500 mb-3" />
                    <h1 className="font-display text-2xl mb-2">Invite unavailable</h1>
                    <p className="text-sm text-stone-500">{err}</p>
                    <Link to="/login" className="inline-block mt-6 text-sm text-stone-900 underline">Go to sign in →</Link>
                </div>
            </div>
        );
    }

    if (!invite) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-400" data-testid="invite-loading">
                <Loader2 size={20} className="animate-spin mr-2" /> Loading invitation…
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-black text-white flex items-center justify-center p-6" data-testid="invite-accept">
            <div className="max-w-md w-full bg-white text-stone-900 rounded-2xl shadow-2xl p-8">
                <div className="text-center mb-6">
                    <div className="text-xs font-semibold tracking-widest text-amber-700 uppercase mb-1">You&rsquo;re invited</div>
                    <h1 className="font-display text-3xl">Join {invite.workspace_name}</h1>
                    <p className="text-sm text-stone-500 mt-2">
                        {invite.invited_by_name} added you as <b>{(invite.role || "").replace("_", " ")}</b>.
                    </p>
                </div>

                {done ? (
                    <div className="text-center py-6" data-testid="invite-success">
                        <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
                        <div className="text-lg font-semibold">Welcome to VAULT!</div>
                        <div className="text-sm text-stone-500 mt-1">Redirecting you to sign in…</div>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-[13px] space-y-1">
                            <div className="flex items-center gap-2"><UserIcon size={12} className="text-stone-500" /><span className="text-stone-700">{invite.name}</span></div>
                            <div className="flex items-center gap-2"><MailIcon size={12} className="text-stone-500" /><span className="text-stone-700">{invite.email}</span></div>
                            <div className="flex items-center gap-2"><Building2 size={12} className="text-stone-500" /><span className="text-stone-700">{invite.workspace_name}</span></div>
                        </div>
                        <div>
                            <Label className="eyebrow">Choose your password *</Label>
                            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} data-testid="invite-pw" />
                            <div className="text-[11px] text-stone-500 mt-1.5">At least 8 characters.</div>
                        </div>
                        <div>
                            <Label className="eyebrow">Confirm password *</Label>
                            <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} data-testid="invite-pw2" />
                        </div>
                        <div className="rounded-lg bg-stone-50 border border-stone-200 p-3 text-[11px] text-stone-600 flex gap-2 items-start">
                            <Lock size={12} className="mt-0.5 text-stone-500 shrink-0" />
                            <span>Passwords are bcrypt-hashed. We never see the plaintext — not even for support.</span>
                        </div>
                        <Button type="submit" disabled={busy} className="w-full bg-stone-900 hover:bg-stone-800 text-white h-11" data-testid="invite-submit">
                            {busy ? <><Loader2 size={14} className="animate-spin mr-2" />Creating account…</> :
                                <><ShieldCheck size={14} className="mr-2" />Accept &amp; create account</>}
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
